import { X } from 'lucide-react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Snackbar, Alert } from "@mui/material"
import ProcessMapUploadForm from '../Forms/ProcessMapUploadForm/ProcessMapUploadForm';
import { useState } from 'react';
import { apiService } from "../../../api/apiService";
import { PublicClientApplication } from "@azure/msal-browser";
import { useMsal } from '@azure/msal-react';


interface UploadExsitingBdPopupProps {
    open: boolean;
    onClose: () => void;
    management: string;
    functionL1: string;
    functionL2: string;
    onSuccess?: () => void;
}

const UploadExsitingBdPopup: React.FC<UploadExsitingBdPopupProps> = ({
    open,
    onClose,
    management,
    functionL1,
    functionL2,
    onSuccess
}) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success" as "success" | "error",
    });

    const { instance } = useMsal();

    const handleFileSelect = (file: File) => {
        const validExcelTypes = [
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ];

        // Check file extension as well (more reliable in some cases)
        const fileName = file.name.toLowerCase();
        const validExtension = fileName.endsWith('.xls') ||
            fileName.endsWith('.xlsx') ||
            fileName.endsWith('.ods');

        if (!validExcelTypes.includes(file.type) && !validExtension) {
            setSnackbar({
                open: true,
                message: "Please select only Excel file formats (.xls, .xlsx, .ods)",
                severity: "error"
            });
            return;
        }

        setSelectedFile(file);
    };

    const resetUploadState = () => {
        setSelectedFile(null);
        setUploadProgress(0);
    };

    const handleSubmit = async () => {
        if (!selectedFile) {
            setSnackbar({
                open: true,
                message: "Please select a file first",
                severity: "error",
            });
            return;
        }

        try {
            setIsSubmitting(true);

            const formData = new FormData();
            formData.append("file", selectedFile);
            formData.append(
                "existing_bd",
                JSON.stringify({
                    management: management,
                    function_l1: functionL1,
                    function_l2: functionL2,
                })
            );
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => Math.min(prev + 10, 90));
            }, 300);

            const response = await apiService(
                "/management/existing_business_desc",
                "POST",
                formData,
                {
                    "Content-Type": "multipart/form-data",
                },
                instance as PublicClientApplication
            );

            clearInterval(progressInterval);
            setUploadProgress(100);

            if (response.status === "success") {
                setSnackbar({
                    open: true,
                    message: "File uploaded successfully!",
                    severity: "success",
                });

                if (onSuccess) {
                    onSuccess();
                }

                setTimeout(() => {
                    onClose();
                    resetUploadState();
                }, 1000);
            } else {
                throw new Error(response.message || "Failed to upload file");
            }

        } catch (error) {
            console.error("Error uploading file:", error);
            setSnackbar({
                open: true,
                message: typeof error === 'string' ? error : "Error uploading file. Please try again.",
                severity: "error",
            });
            setUploadProgress(0);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#000' }}>
                    Upload Business Description
                    <IconButton onClick={onClose}>
                        <X size={20} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <ProcessMapUploadForm
                            file={selectedFile}
                            onFileSelect={handleFileSelect}
                            onFileRemove={() => {
                                setSelectedFile(null);
                                setUploadProgress(0);
                            }}
                            uploadProgress={uploadProgress}
                            fileTitle={'Upload Business Description File'}
                            placeholder="File types: XLSX, XLS"
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        variant='outlined'
                        onClick={onClose}
                        sx={{ color: '#000', borderColor: '#000' }}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!selectedFile || isSubmitting}
                        sx={{
                            color: 'white',
                            bgcolor: 'black',
                            '&:hover': { bgcolor: 'black' },
                            '&:disabled': { bgcolor: 'grey.300' },
                        }}
                    >
                        {isSubmitting ? 'Uploading...' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default UploadExsitingBdPopup;